import { useMemo } from 'react';
import _ from 'lodash';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Grid, TextInput } from '@ts/storybook';
import StatusInput from '../../Status/StatusInput';
import { addNewFlavorTabs } from '../flavor.utils';

import ItemTypeTab from './ItemTypeGeneralTab';
import FlavorSelect from '../FlavorSelect';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import Misc from '@components/Misc';
import { CElementType, CStatusIndicator, IForm } from '@ts/ui-models';

interface IGeneralTabProps extends WithTranslation, IForm {
  values: CElementType;
  onChangeTabs: (tabs: any[], action: 'add' | 'remove') => void;
}

const GeneralTab = (props: IGeneralTabProps) => {
  const { values: tElementType } = props;
  const isReadOnly = props.mode === 'view';

  //#region Handlers
  //#endregion

  //#region Fuctions
  const getDefaultProps = () => {
    const searchComponentConfig = useMemo(() => {
      const config = generateSearchEntitySelector('tag');
      return {
        component: config.component,
        configComponent: {
          ...config.configComponent,
          where: {
            restrictedToEntities: 'tsElement',
            //elementTypeId: elementTypeId,
          },
        },
      };
    }, []);

    const rows = useMemo(
      () => [
        {
          id: 'name',
          name: props.t('Name'),
          lockable: true,
          readable: true,
          seriable: false,
          value: (disabled = false, valuesIn) => (
            <TextInput
              isReadOnly={disabled}
              value={valuesIn.name ?? ''}
              onChangeValue={v => {
                props.onCallbackChange('name')(v);
                props.onCallbackChange('customId')(v);
              }}
              required={props.requiredFields?.includes('name')}
              id={'itemType-nameInput'}
              infoText={'Blank spaces at the beggining and the end will be trimmed on save'}
            />
          ),
        },
        {
          id: 'customId',
          name: props.t('Custom ID'),
          lockable: false,
          readable: false,
          seriable: true,
          value: (disabled = false, valuesIn) => (
            <TextInput
              isReadOnly={disabled}
              value={valuesIn.customId ?? ''}
              onChangeValue={props.onCallbackChange('customId')}
              required={props.requiredFields?.includes('customId')}
              id={'itemType-customIdInput'}
              infoText="Blank spaces at the beggining and the end will be trimmed on save"
            />
          ),
        },
        {
          id: 'tagIds',
          name: props.t('Labels'),
          lockable: true,
          readable: true,
          seriable: false,
          value: (disabled = false, valuesIn, dataIn) => (
            <WrapperEntitySelector
              value={valuesIn.tagIds}
              searchComponent={searchComponentConfig}
              onChange={props.onCallbackChange('tagIds')}
              isReadOnly={disabled}
              multiple
            />
          ),
        },
        {
          id: 'notes',
          name: props.t('Notes'),
          lockable: true,
          readable: true,
          seriable: false,
          value: (disabled = false, valuesIn) => (
            <TextInput
              label={'Notes'}
              value={valuesIn.notes ?? ''}
              multiline
              disabled={disabled}
              onChange={props.onCallbackChange('notes')}
            />
          ),
        },
      ],
      [props.requiredFields]
    );

    return {
      hasUnique: true,
      rows: rows,
      values: props.values,
      isViewMode: props.mode === 'view',
    };
  };

  const getFlavorsKeys = () => {
    let keysAlive: any[] = [];
    let flavors = props.values.flavors;

    for (let flavor in flavors) {
      if (flavors[flavor]) keysAlive.push(flavor);
    }
    return keysAlive;
  };

  const _getFlavors = () => {
    if (!props.values || !props.values.flavors) return [];

    return Object.keys(props.values.flavors).reduce((acc: any[], value) => {
      if (props.values.flavors[value]) {
        acc.push({ name: value });
      }
      return acc;
    }, []);
  };

  const addFlavor = (flavorName: string) => {
    let mainFlavors = props.values.mainFlavor || null;
    let tabsToAdd = addNewFlavorTabs('itemType', [], `${flavorName}`);
    props.onChangeTabs(tabsToAdd, 'add');
    if (!mainFlavors) props.onCallbackChange('mainFlavor')(flavorName);
  };

  const removeFlavor = (flavorName: string) => {
    const propFlavors = _getFlavors();

    let tabsToRemove = addNewFlavorTabs('itemType', [], `${flavorName}`);
    if (tabsToRemove.length === 0) tabsToRemove = addNewFlavorTabs('itemType', [], `${flavorName}`);
    if (tabsToRemove.length > 0) {
      props.onChangeTabs(tabsToRemove, 'remove');
    }
    _.remove(propFlavors, flv => {
      return flv.name === flavorName;
    });
    props.onCallbackChange('mainFlavor')(propFlavors[0] ? propFlavors[0].name : null);
  };
  //#endregion

  //#region Renders
  const renderStatusInput = () => {
    return (
      <Grid size={{ xs: 12 }} mt={2}>
        <Misc.NewPaperWithPadding>
          <StatusInput
            mode={props.mode}
            title={'Status'}
            statesList={tElementType.currentStatus.indicators.map(i => new CStatusIndicator(i))}
            onChangeList={newList => {
              let newStatus = _.clone(props.values.currentStatus);
              if (newStatus) newStatus.indicators = newList;
              return props.onCallbackChange('currentStatus')(newStatus);
            }}
          />
        </Misc.NewPaperWithPadding>
      </Grid>
    );
  };

  //#endregion

  return (
    <>
      {props.mode === 'new' && (
        <FlavorSelect
          selectedFlavors={getFlavorsKeys()}
          addFlavor={addFlavor}
          removeFlavor={removeFlavor}
          isReadOnly={isReadOnly}
        />
      )}
      <ItemTypeTab {...props} {...getDefaultProps()} />
      {renderStatusInput()}
    </>
  );
};

export default withTranslation()(GeneralTab);
